export type LegacyAnalyticsEventData =
  | LegacyDGCAnalyticsEventData
  | LegacyAuthAnalyticsEventData
  | LegacyMKPAnalyticsEventData

export type LegacyMKPAnalyticsEventData =
  | ({ event: 'view_item' } & MkpViewItem)
  | ({ event: 'add_to_cart' } & MkpAddToCart)
  | ({ event: 'remove_from_cart' } & MkpRemoveFromCart)
  | ({ event: 'sendhitCheckout' } & MkpSendHitCheckout)
  | ({ event: 'purchase' } & MkpPurchase)
  | ({
      event: 'SearchInteraction'
    } & MkpSearchInteractionCentreSearchResultClicked)
  | ({ event: 'product_list_viewed' } & MkpProductListViewed)

export type LegacyDGCAnalyticsEventData =
  | ({ event: 'view_item' } & DgcViewItem)
  | ({ event: 'add_to_cart' } & DgcAddToCart)
  | ({ event: 'remove_from_cart' } & DgcRemoveFromCart)
  | ({
      event: 'view_cart' | 'begin_checkout' | 'add_payment_info'
    } & DgcViewCartBeginCheckoutOrAddPaymentInfo)
  | ({ event: 'purchase' } & DgcPurchase)

type LegacyAuthAnalyticsEventData = {
  event: 'user_interaction'
} & AuthUserInteraction

export type IEcommerceProduct = {
  name: string
  id: string
  uniqueId: string
  price: number | null | undefined
  brand: string | null | undefined
  category: string | null | undefined
  variant?: string
  /** Retailer name */
  dimension127?: string | null | undefined
  /** Retailer id */
  dimension128?: string | null | undefined
  dimension129: string
  /** Checkout option */
  dimension135?: CheckoutOption | ''
  /** Product Variant Group Code */
  dimension136?: string | null | undefined
  /** Product SKU */
  dimension137?: string | null | undefined
  /** Mirakl Shop ID */
  dimension138?: string | number | null | undefined
} & IEcommerceProductPromotion

type IEcommerceProductPromotion = {
  /** Promotion campaign id - auto */
  dimension131?: string | null | undefined
  /** Promotion campaign title - auto */
  dimension132?: string | null | undefined
  /** Promotion campaign id - coupon-based */
  dimension133?: string | null | undefined
  /** Promotion campaign title - coupon-based */
  dimension134?: string | null | undefined
  coupon?: string | null | undefined
}

export type IEcommerceDGCProduct = {
  name: string
  id: string
  price?: string
  brand: string
  category: string
  dimension91?: string
  dimension92?: string
  dimension93?: string
  dimension94?: string
  dimension95?: string
  dimension96?: string
  dimension97?: string
  dimension98?: string
  dimension99?: string
  dimension100?: string
  dimension116?: string
  dimension117?: string
}

enum CheckoutOption {
  APPLE_PAY = 'Apple Pay',
  CREDIT_CARD = 'Credit Card',
  GOOGLE_PAY = 'Google Pay',
}

export const DGC_AFFILIATION = 'Westfield - Digital Gift Card - AU'
type DGC_AFFILIATION = typeof DGC_AFFILIATION

export const isDGCEvent = (
  event: LegacyAnalyticsEventData
): event is LegacyDGCAnalyticsEventData => {
  return !!(
    (event as LegacyDGCAnalyticsEventData).ecommerce?.affiliation ===
    DGC_AFFILIATION
  )
}

// Soon we should be able to delete the non-interface
// versions of these types soon
export interface LegacyEventProperties {
  mkp_view_item: { event: 'view_item' } & MkpViewItem
  mkp_add_to_cart: { event: 'add_to_cart' } & MkpAddToCart
  mkp_remove_from_cart: { event: 'remove_from_cart' } & MkpRemoveFromCart
  mkp_sent_hit_checkout: { event: 'sendhitCheckout' } & MkpSendHitCheckout
  mkp_purchase: { event: 'purchase' } & MkpPurchase
  mkp_search_interaction_centre_search_result_clicked: {
    event: 'SearchInteraction'
  } & MkpSearchInteractionCentreSearchResultClicked
  mkp_product_list_viewed: {
    event: 'product_list_viewed'
  } & MkpProductListViewed
  dgc_view_item: { event: 'view_item' } & DgcViewItem
  dgc_add_to_cart: { event: 'add_to_cart' } & DgcAddToCart
  dgc_remove_from_cart: { event: 'remove_from_cart' } & DgcRemoveFromCart
  dgc_view_cart_begin_checkout_or_add_payment_info: {
    event: 'view_cart' | 'begin_checkout' | 'add_payment_info'
  } & DgcViewCartBeginCheckoutOrAddPaymentInfo
  dgc_purcahase: { event: 'purchase' } & DgcPurchase
  auth_user_interaction: {
    event: 'user_interaction'
  } & AuthUserInteraction
}

type MkpViewItem = {
  ecommerce: {
    currencyCode: string
    affiliation: string
    detail: {
      products: IEcommerceProduct[]
    }
  }
}

type MkpAddToCart = {
  cartId?: string | null | undefined
  ecommerce: {
    currencyCode: string
    affiliation: string
    add: {
      products: (IEcommerceProduct & { quantity: number })[]
    }
  }
}

type MkpRemoveFromCart = {
  cartId: string
  ecommerce: {
    currencyCode: string
    affiliation: string
    remove: {
      products: (IEcommerceProduct & { quantity: number })[]
    }
  }
}

type MkpSendHitCheckout = {
  cartId: string
  ecommerce: {
    currencyCode: string
    checkout: {
      actionField: {
        step: string
        option: 'loggedin' | 'guest'
      }
      products: (IEcommerceProduct & { quantity: number })[]
    }
  }
}

type MkpPurchase = {
  cartId?: string | undefined
  ecommerce: {
    currencyCode: string
    affiliation: 'Westfield - Direct - AU'
    purchase: {
      actionField: {
        id: string
        affiliation: 'Westfield - Direct - AU'
        revenue: string
        tax: string
        shipping: string
        coupon?: string | undefined
      }
      products: (IEcommerceProduct & { quantity: number })[]
      deliveryType: string
      collectLocation?: string
      paymentMethod: string
      shippingAddress?: {
        address1: string
        suburb: string
        state: string
        postCode: string
        country: string
      }
      billingAddress: {
        address1: string
        suburb: string
        state: string
        postCode: string
        country: string
      }
    }
  }
}

type MkpSearchInteractionCentreSearchResultClicked = {
  eventCategory: 'centreSearch'
  eventAction: 'searchResultClicked'
  eventLabel: string
  centreResultClicked: string
}

type MkpProductListViewed = {
  ecommerce: {
    currencyCode: string
    affiliation: string
    listId: string
    sortMethod: string
    detail: {
      products: IEcommerceProduct[]
    }
  }
}
type DgcViewItem = {
  ecommerce: {
    currencyCode: string
    affiliation: DGC_AFFILIATION
    detail: {
      products: IEcommerceDGCProduct[]
    }
  }
}

type DgcAddToCart = {
  ecommerce: {
    currencyCode: string
    affiliation: DGC_AFFILIATION
    add: {
      products: (IEcommerceDGCProduct & { quantity: number })[]
    }
  }
}

type DgcRemoveFromCart = {
  ecommerce: {
    currencyCode: string
    affiliation: DGC_AFFILIATION
    remove: {
      products: (IEcommerceDGCProduct & { quantity: number })[]
    }
  }
}

type DgcViewCartBeginCheckoutOrAddPaymentInfo = {
  ecommerce: {
    currencyCode: string
    affiliation: DGC_AFFILIATION
    checkout: {
      actionField: { step: 1 | 2 }
      products: (IEcommerceDGCProduct & { quantity: number })[]
    }
  }
}

type DgcPurchase = {
  ecommerce: {
    currencyCode: string
    affiliation: DGC_AFFILIATION
    purchase: {
      actionField: {
        id: string
        cartId: string | undefined
        affiliation: DGC_AFFILIATION
        revenue: string
        tax: string
        shipping: string
        coupon?: string | undefined
      }
      products: (IEcommerceDGCProduct & { quantity?: number })[]
    }
  }
}

type AuthUserInteraction = {
  eventCategory: 'Westfield Plus'
  eventAction: 'Login' | 'Sign Up'
  eventLabel: string
  eventCallback: () => void
  eventTimeout: number
}
